/**
 * Cross-context copy-to-clipboard with a hidden-textarea fallback.
 *
 * `navigator.clipboard.writeText` is unavailable in non-secure contexts
 * (some mobile previews, iframes) and may be silently denied on iOS
 * Safari without user activation. This helper tries the modern API first
 * and falls back to a transient `<textarea>` + `document.execCommand("copy")`
 * so users always have a path to copy the value.
 *
 * Resolves true on success, false on failure. Never throws.
 */
export async function copyToClipboard(value) {
  if (value == null) return false;
  const text = String(value);
  // Modern API first.
  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through
    }
  }
  // Hidden-textarea fallback. execCommand is deprecated but still works in
  // every browser ChalkOS targets, including iframes / non-secure contexts.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand && document.execCommand("copy");
    document.body.removeChild(ta);
    return Boolean(ok);
  } catch {
    return false;
  }
}
